import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { isImpersonating } from '../utils/impersonation';
import './PortalAccount.css';

const DETAIL_FIELDS = [
  ['first_name', 'First name', true],
  ['last_name', 'Last name', true],
  ['companyName', 'Company'],
  ['phoneNumber', 'Phone'],
  ['address1', 'Address line 1'],
  ['address2', 'Address line 2'],
  ['city', 'Town / city'],
  ['county', 'County'],
  ['postcode', 'Postcode'],
  ['country', 'Country']
];

const MIN_PASSWORD = 10;

function PortalAccount() {
  const navigate = useNavigate();
  const readOnly = isImpersonating();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [twoFactor, setTwoFactor] = useState(null);
  const [detailsMsg, setDetailsMsg] = useState(null);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const me = await apiClient.get('/api/users/me');
        setProfile(me);
        setForm(Object.fromEntries(DETAIL_FIELDS.map(([key]) => [key, me[key] || ''])));
      } catch (error) {
        setDetailsMsg({ type: 'error', text: 'Could not load your details.' });
      }
      apiClient.get('/api/auth/2fa/status').then(setTwoFactor).catch(() => setTwoFactor(null));
    })();
  }, []);

  const saveDetails = async (event) => {
    event.preventDefault();
    setSaving(true);
    setDetailsMsg(null);
    try {
      const updated = await apiClient.put('/api/users/me', form);
      setProfile(updated);
      Globals.FirstName = updated.first_name;
      Globals.LastName = updated.last_name;
      setDetailsMsg({ type: 'ok', text: 'Your details have been saved.' });
    } catch (error) {
      setDetailsMsg({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setPwMsg(null);
    if (pw.newPassword.length < MIN_PASSWORD) {
      return setPwMsg({ type: 'error', text: `New password must be at least ${MIN_PASSWORD} characters.` });
    }
    if (pw.newPassword !== pw.confirm) {
      return setPwMsg({ type: 'error', text: "The new passwords don't match." });
    }
    setSaving(true);
    try {
      await apiClient.post('/api/users/change-password', {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword
      });
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      setPwMsg({ type: 'ok', text: 'Password changed.' });
    } catch (error) {
      setPwMsg({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const twoFactorLabel = () => {
    if (!twoFactor) return 'Unavailable';
    if (!twoFactor.twoFactorEnabled) return 'Not set up';
    const methods = [];
    if (twoFactor.hasTotpEnabled) methods.push('authenticator app');
    if (twoFactor.hasWebauthnEnabled) methods.push('security key');
    return `On (${methods.join(' and ') || twoFactor.twoFactorType})`;
  };

  return (
    <PortalShell>
      <h1>My Account</h1>

      <section className="portal-account-section">
        <h2>Your details</h2>
        <form onSubmit={saveDetails}>
          <FormField id="acct-email" label="Email (used to log in)">
            <input id="acct-email" value={profile?.email || ''} disabled readOnly />
          </FormField>
          <p className="portal-account-hint">To change your login email, please contact us.</p>
          <div className="portal-account-grid">
            {DETAIL_FIELDS.map(([key, label, required]) => (
              <FormField key={key} id={`acct-${key}`} label={label} required={required}>
                <input
                  id={`acct-${key}`}
                  value={form[key] || ''}
                  maxLength={255}
                  required={required}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </FormField>
            ))}
          </div>
          {detailsMsg && <p role="status" className={`portal-account-msg portal-account-msg-${detailsMsg.type}`}>{detailsMsg.text}</p>}
          {!readOnly && <Button type="submit" disabled={saving || !profile}>Save details</Button>}
        </form>
      </section>

      <section className="portal-account-section">
        <h2>Password</h2>
        <form onSubmit={changePassword}>
          <FormField id="acct-current" label="Current password">
            <input id="acct-current" type="password" autoComplete="current-password" value={pw.currentPassword} disabled={readOnly} required
              onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
          </FormField>
          <FormField id="acct-new" label={`New password (at least ${MIN_PASSWORD} characters)`}>
            <input id="acct-new" type="password" autoComplete="new-password" value={pw.newPassword} disabled={readOnly} required
              onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
          </FormField>
          <FormField id="acct-confirm" label="Confirm new password">
            <input id="acct-confirm" type="password" autoComplete="new-password" value={pw.confirm} disabled={readOnly} required
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </FormField>
          {pwMsg && <p role="status" className={`portal-account-msg portal-account-msg-${pwMsg.type}`}>{pwMsg.text}</p>}
          {!readOnly && <Button type="submit" disabled={saving}>Change password</Button>}
        </form>
      </section>

      <section className="portal-account-section">
        <h2>Two-factor authentication</h2>
        <p>Status: <strong>{twoFactorLabel()}</strong></p>
        <p className="portal-account-hint">Two-factor is required on all accounts. If you've lost your device, contact us and we'll reset it.</p>
      </section>
    </PortalShell>
  );
}

export default PortalAccount;
